
import { mapState, mapMutations, mapActions } from 'vuex'
import Radar from '~/node_modules/radar-sdk-js'
import moment from 'moment'

import FormField from '../../_globals/forms/FormField.vue'
import Icon from '../Icon'

export default {
  name: 'LocationDeliveryAddress',
  components: { FormField, Icon },
  props: {
    action: Object,
    config: {
      type: Object,
    },
  },
  data() {
    return {
      isEdit: true,
      query: '',
      lat: '',
      lng: '',
      complementField: {
        name: 'complement',
        label: 'Suite/Apt/Door (Optional)',
        type: 'text',
        value: '',
        placeholder: 'Enter Suite/Apt/Door (optional)',
        valid: /^[A-Za-z0-9]{20}/,
        errors: {
          invalid: 'Please enter a valid suite/apt/door.'
        }
      },
      cityField: {
        name: 'city',
        label: 'City',
        type: 'text',
        value: '',
        placeholder: 'Enter City',
        required: true,
        valid: /^[A-Za-z]{40}/,
        errors: {
          invalid: 'Please enter a valid city.'
        }
      },
      stateField: {
        name: 'state',
        label: 'State',
        type: 'text',
        value: '',
        placeholder: 'Enter State',
        required: true,
        valid: /^[A-Z]{2}/,
        errors: {
          invalid: 'Please enter a valid state.'
        }
      },
      zipCodeField: {
        name: 'zipCode',
        label: 'Zip code',
        type: 'text',
        value: '',
        placeholder: 'Enter Zip code',
        required: true,
        valid: /^[0-9]{5}/,
        errors: {
          invalid: 'Please enter a valid zip code.'
        }
      },
      phoneField: {
        name: 'phone',
        label: 'Phone Number',
        value: '',
        placeholder: 'Enter Phone Number',
        type: 'text',
        mask: '###-###-####',
        required: true,
        valid: /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
        errors: {
          invalid: 'Please enter a valid US phone number'
        }
      },
      instructionsField: {
        name: 'instructions',
        label: 'Delivery Instructions (optional)',
        type: this.config?.location?.instructionFieldTextArea ? 'textarea' : 'text',
        value: '',
        placeholder: 'Enter Instructions',
        valid: /^[A-Za-z0-9]{20}/,
        errors: {
          invalid: 'Please enter a valid instructions.'
        }
      },
    }
  },
  mounted() {
    const radarKey = process.env.RADAR_KEY
    if (!radarKey) {
      this.$utils.log('LocationDeliveryForm - RADAR_KEY is missing')
      return
    }

    if (this.$route.query.address == 'new') {
      setTimeout(() => {
        this.isEdit = true
      },250)
    }

    setTimeout(() => {
      // Initialize Radar SDK
      Radar.initialize(radarKey)

      // Create autocomplete widgets
      Radar.ui.autocomplete({
        container: 'autocomplete-delivery',
        countryCode: this.appRadar['country'] || '',
        layers: this.appRadar['layers'] || '',
        responsive: true,
        placeholder: 'Enter Address',
        onSelection: (result) => {
          document.querySelector('#autocomplete-delivery input').value = result.addressLabel
          this.query = result.addressLabel
          this.cityField.value = result.city
          this.stateField.value = result.stateCode
          this.zipCodeField.value = result.postalCode
          this.lat = result.latitude
          this.lng = result.longitude
        },
      })

      // Add autofill functionality
      this.$nextTick(() => {
        document.addEventListener('keyup', (e) => {
          if (e.target.parentElement.parentElement?.id == 'autocomplete-delivery') {
            this.query = e.target.value
          }
        })
      })

      if (this.cart && this.cart.address) {
        return this.updateForm(this.cart.address)
      }

      if (this.deliveryAddress) {
        return this.updateForm(this.deliveryAddress)
      }

      this.isEdit = true
    }, 200)
  },
  beforeDestroy() {
    document.removeEventListener('keyup', () => {})
    if (!this.cart || (this.cart?.handoff !== 'delivery' && !this.cart?.address)) {
      this.setHandoffMode(this.cart?.handoff || 'pickup')
    }
  },
  computed: {
    ...mapState([ 'loading', 'cart', 'appInfo', 'channel', 'menu' ]),
    ...mapState({
      deliveryAddress: state => state.locations.deliveryAddress
    }),
    appRadar() {
      return this.appInfo['radar_map']
    },
    orderDeliveryDisabled() {
      if (this.loading) return true

      if (!this.query ||
          !this.cityField.value ||
          !this.stateField.value ||
          !this.zipCodeField.value ||
          !this.phoneField.value
        ) return true

      return false
    },
  },
  methods: {
    ...mapActions([ 'getCart', 'getDeliveryLocations', 'trackEvent' ]),
    ...mapActions({
      applyCartHandoff: 'checkout/applyCartHandoff',
    }),
    ...mapMutations({
      setDeliveryAddress: 'locations/setDeliveryAddress',
      setHandoffMode: 'order/setHandoffMode'
    }),
    edit() {
      this.isEdit = !this.isEdit
    },
    async handleSubmit() {
      // Times set on lines 202-211 will get overwritten when ASAP/Order Ahead time is chosen by user
      let time = null
      const night = moment().format('YYYYMMDD') + ' 20:00' // 10pm
      const noon = moment().format('YYYYMMDD') + ' 12:00' // Noon
      const nextNoon = moment().add(1, 'day').format('YYYYMMDD') + ' 12:00' // Noon the next day

      if (moment().isBefore(moment(noon, 'YYYYMMDD H:mm'))) { // If before noon of the current day, set time to noon to allow delivery search before locations open
        time = noon
      } else if (moment().isAfter(moment(night, 'YYYYMMDD H:mm'))) { // If after 10pm of the current day, set time to noon tomorrow to allow delivery search after locations close
        time = nextNoon
      }

      if (this.channel === 'catering') {
        time = moment().add(3, 'day').format('YYYYMMDD') + ' 12:00' // Two days in advance for catering
      }

      const location = await this.getDeliveryLocations({
        address: this.query,
        city: this.cityField.value,
        zip: this.zipCodeField.value,
        lat: this.lat,
        lng: this.lng,
        time,
      })

      if (location === null) return

      this.isEdit = false
      this.setDeliveryAddress({
        address: this.query,
        building: this.complementField.value,
        city: this.cityField.value,
        state: this.stateField.value,
        zip: this.zipCodeField.value,
        phone: this.phoneField.value,
        instructions: this.instructionsField.value,
        lat: this.lat,
        lng: this.lng,
      })

      await this.applyCartHandoff({
        address: this.query,
        building: this.complementField.value,
        city: this.cityField.value,
        state: this.stateField.value,
        zip: this.zipCodeField.value,
        phone: this.phoneField.value,
        instructions: this.instructionsField.value,
        default: false,
        handoffMode: 'delivery',
      })

      this.trackEvent({
        name: 'add_shipping_info',
        windowObject: window,
        cart: this.cart,
        menu: this.menu,
      })

      await this.getCart()
    },
    updateForm({ address, building, city, state, zip, phone, instructions, lat, lng }) {
      document.querySelector('#autocomplete-delivery input').value = address
      this.query = address
      this.complementField.value = building
      this.cityField.value = city
      this.stateField.value = state
      this.zipCodeField.value = zip
      this.phoneField.value = phone
      this.instructionsField.value = instructions
      this.lat = lat
      this.lng = lng

      this.setDeliveryAddress({ address, building, city, state, zip, phone, instructions, lat, lng })

      this.isEdit = false
    },
  },
}
